import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CookiePolicyService } from '@core/cookie/cookie-policy.service';
import { datadogRum, RumInitConfiguration } from '@datadog/browser-rum';
import { EnvironmentLoaderService } from 'ok-angular-shared/services';
import { filter } from 'rxjs';
import { AppSettings } from 'src/settings/appsettings.interface';

@Injectable()
export class DatadogService {
  private environmentLoaderService = inject<EnvironmentLoaderService<AppSettings>>(EnvironmentLoaderService);
  private cookiePolicyService = inject(CookiePolicyService);
  private destroyRef = inject(DestroyRef);
  private rumInitialized = false;

  constructor() {}

  public async init() {
    this.cookiePolicyService.consentGiven
      .pipe(
        filter(consent => consent && !this.rumInitialized && !!this.environmentLoaderService.config.dataDogClientToken),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.initRum();
        this.rumInitialized = true;
      });
  }

  private initRum() {
    const config = {
      applicationId: this.environmentLoaderService.config.dataDogApplicationId,
      clientToken: this.environmentLoaderService.config.dataDogClientToken,
      service: this.environmentLoaderService.config.dataDogService,
      env: this.environmentLoaderService.config.environment,
      site: 'datadoghq.eu',
      version: this.environmentLoaderService.buildData().version,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 0,
      trackResources: true,
      trackLongTasks: true,
      trackUserInteractions: true,
      actionNameAttribute: 'dd-action',
      defaultPrivacyLevel: 'mask-user-input',
      allowedTracingUrls: [{ match: this.environmentLoaderService.config.subscriptionServiceBaseUrl, propagatorTypes: ['tracecontext'] }],
    } as RumInitConfiguration;

    datadogRum.init(config);
  }

  public errorMessage(message: string, context?: { [x: string]: unknown }): void {
    if (this.rumInitialized) {
      datadogRum.addError(message, context);
    }
  }

  public error(error: Error, context?: { [x: string]: unknown }): void {
    if (this.rumInitialized) {
      datadogRum.addError(error, context);
    }
  }
}
